<template>
	<section
		:class="[
			'pdp-content-boxes',
			'box',
			{ 'pdp-content-boxes-shadow': enableShadow },
			{ 'pdp-content-boxes-animation': enableAnimation }
		]"
	>
		<div class="col sm-auto">
			<ContentBox :boxData="box1Data" @button-click="onButtonOneClicked" />
		</div>
		<div class="col sm-auto">
			<ContentBox :boxData="box2Data" @button-click="onButtonTwoClicked" />
		</div>
	</section>
</template>

<script>
import ContentBox from "./ContentBox";

/**
 * The Content Box 2-Up is a template that provides a heading, paragraph text, and CTA button within two styled boxes that appear side-by-side or stacked in smaller viewports.
 *
 * ## `box1Data`/`box2Data` props
 *
 * The `boxData` object the accepts the following properties:
 *
 * - **boxID** {String} (optional) the ID for the content box
 *
 * - **fgColor** {String} (optional) the foreground color name (can be `white` or `almost-black`)
 *
 * - **bgColor** {String} background color variable name (refer to the @vg-faset/core color palette)
 *
 * - **bgPosition** {String|Object} (optional) enter 'right' or 'left', or define the bgPosition for each breakpoint in an object `{ sm: 'center bottom', md: 'left bottom', lg: 'left bottom' }`
 *
 * - **graphic** {String} graphic url
 *
 * - **bgSize** {Object} background size object for each breakpoint (sm/md/lg)
 *
 * - **bgOverlayColor** {String} (optional) color overlay (hexadecimal color value with hash, ex `#ffffff`). sits over the background image but behind the text content
 *
 * - **bgOverlayOpacity** {String} (optional) opacity value for the background color overlay (value range 0.0 to 1.0)
 *
 * - **title** {String} the `<h3>` title of the content box
 *
 * - **content** {String|Array} the `<p>` content of the content box. Pass an array to create multiple `<p>`
 *
 * - **contentMarginBottom** {Object} (optional) custom margin bottom for below the content paragraph element. Define the margin-bottom for each breakpoint in an object `{ sm: '80px', md: '80px', lg: '180px' }`
 *
 * - **maxWidth** {String|Object} (optional) set the max width of the content as a string `80%`, or as an object for each breakpoint `{ sm: '100%', md: '100%', lg: '80%' }`
 *
 * - **ctaUrl** {String} the href for the CTA button
 *
 * - **ctaText** {String} the text of the CTA button
 *
 * - **ctaDescription** {String} description of the CTA link functionality (for accessibility - read by screen readers)
 *
 * - **ctaLocked** {Boolean} whether to display the lock icon that denotes secure content
 *
 */
export default {
	name: "ContentBox2up",
	props: {
		/**
		 * The content object for the first content box template. Refer to the available properties for this object above.
		 */
		box1Data: {
			type: Object,
			required: false
		},

		/**
		 * The content object for the second content box template. Refer to the available properties for this object above.
		 */
		box2Data: {
			type: Object,
			required: false
		},

		/**
		 * Add or disable shadow. Default to `false`
		 */
		enableShadow: {
			type: Boolean,
			default: false
		},

		/**
		 * Add or disable animation for the box. Default to `false`. Animation will only work on desktop viewport.
		 */
		enableAnimation: {
			type: Boolean,
			default: false
		}
	},
	components: { ContentBox },
	methods: {
		onButtonOneClicked(e) {
			this.$emit("button-box-1-click", e);
		},
		onButtonTwoClicked(e) {
			this.$emit("button-box-2-click", e);
		}
	}
};
</script>

<style lang="scss">
.pdp-content-boxes {
	display: flex;
	flex-wrap: wrap;
	justify-content: space-between;

	// When a 2up or 1up follows a 2up
	+ .pdp-content-boxes,
	+ .pdp-content-box {
		margin-top: 2px;

		@include from-md {
			margin-top: 80px;
		}
	}

	.pdp-content-box {
		height: 100%;
		margin-bottom: 0;
	}

	@include to-md {
		.col {
			flex: 1 0 100%;

			&:first-child {
				margin-bottom: 2px;
			}
		}
	}

	@include from-md {
		.col:first-child {
			margin-right: 32px;
		}
	}

	@include from-lg {
		.col:first-child {
			margin-right: 80px;
		}
	}
}

.pdp-content-boxes-shadow {
	.pdp-content-box {
		box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.5);
	}
}

.pdp-content-boxes-animation {
	.pdp-content-box {
		@include from-lg() {
			transform: translateY(0px);
			transition: transform 0.2s ease-in-out 0s;
		}

		&:hover {
			@include from-lg() {
				transform: translateY(-8px);
			}
		}
	}
}
</style>
